import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router";
import { INotificationMultipleReadRequest } from "Data/interfaces/Notification/INotification";
import { useQueryReadMultipleNotifications } from "Hooks/notifications/useQueryReadMultipleNotifications";
import dayjs, { Dayjs } from "dayjs";

const MAX_PROCESSED_ITEMS = 200;
const MAX_ClEAR_PROCESSED_ITEMS = 80;
const MAX_AGE_MINUTES = 30;

interface NotificationQueueItem {
  key: string;
  path: string;
  notifications: INotificationMultipleReadRequest;
  timestamp: number;
}

interface NotificationContextType {
  addNotificationsToRead: (
    key: string,
    notifications: INotificationMultipleReadRequest,
    path?: string,
  ) => void;
  processNotifications: () => Promise<void>;
  isProcessing: boolean;
  queueSize: number;
}

const createContentHash = (notifications: INotificationMultipleReadRequest) => {
  return notifications.notificationIds
    .map(n => `${notifications.constructionSiteId}_${n}_${notifications.itemAuxId || ''}`)
    .sort()
    .join('|');
};

const NotificationContext = createContext<NotificationContextType>(null!);

export const NotificationProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const { readMultipleNotificationsAsync } = useQueryReadMultipleNotifications();

  const processingRef = useRef(false);
  const [queue, setQueue] = useState<NotificationQueueItem[]>([]);
  const [processedHashes, setProcessedHashes] = useState(new Map<string, Dayjs>());
  const [isProcessing, setIsProcessing] = useState(false);

  const addNotificationsToRead = useCallback((
    key: string,
    notifications: INotificationMultipleReadRequest,
    path?: string,
  ) => {
    if (!notifications.notificationIds || notifications.notificationIds.length === 0) return;

    const contentHash = createContentHash(notifications);
    if (processedHashes.has(contentHash)) return;

    const queueItem: NotificationQueueItem = {
      key,
      path: path || location.pathname,
      notifications,
      timestamp: Date.now()
    };

    setQueue(prev => {
      const filtered = prev.filter(item => !(item.key === key && item.path === queueItem.path));
      return [...filtered, queueItem];
    });

    setProcessedHashes(prev => {
      const novoSet = new Map(prev);
      novoSet.set(contentHash, dayjs());
      return novoSet;
    });
  }, [location.pathname, processedHashes]);

  const processQueue = useCallback(async () => {
    if (processingRef.current || queue.length === 0) return;

    processingRef.current = true;
    setIsProcessing(true);

    try {
      const itemsToProcess = queue.filter(item => item.path !== location.pathname);
      if (itemsToProcess.length === 0) return;

      setQueue(prev => prev.filter(item => item.path === location.pathname));

      for (const queueItem of itemsToProcess) {
        await readMultipleNotificationsAsync(queueItem.notifications);
      }
    } catch (error) {
      console.error('Erro geral no processamento da fila:', error);
    } finally {
      processingRef.current = false;
      setIsProcessing(false);
    }
  }, [queue, readMultipleNotificationsAsync, location.pathname]);

  const processNotifications = useCallback(async () => {
    await processQueue();
  }, [processQueue]);

  const clearProcessedHashes = useCallback(() => {
    if (processedHashes.size > MAX_ClEAR_PROCESSED_ITEMS) {
      setProcessedHashes(prev => {
        const arrayHashes = Array.from(prev.entries());
        const sortedArrayHashes = arrayHashes.sort((a, b) => {
          return b[1].isAfter(a[1]) ? 1 : -1;
        });

        if (sortedArrayHashes.length > MAX_PROCESSED_ITEMS) {
          const slicedArrayHashes = sortedArrayHashes.slice(0, MAX_PROCESSED_ITEMS);
          return new Map(slicedArrayHashes);
        } else {
          const now = dayjs();
          const filteredArrayHashes = sortedArrayHashes.filter(([hash, date]) => {
            return now.diff(date, 'minutes') < MAX_AGE_MINUTES;
          });
          return new Map(filteredArrayHashes);
        }
      });
    }
  }, [processedHashes]);

  useEffect(() => {
    processNotifications();
    clearProcessedHashes();
  }, [location.pathname, processNotifications, clearProcessedHashes]);

  return (
    <NotificationContext.Provider value={{
      addNotificationsToRead,
      processNotifications,
      isProcessing,
      queueSize: queue.length
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
